import { useState, useEffect, useCallback } from 'react';
import { useEffectiveBusinessId } from '@/hooks';
import { getUnassignedPhotos, assignPhotoToProject, markPhotoUnassigned, getPhotoUrl } from '../services/photoService';
import type { UnassignedPhotoView } from '../types';

interface UseUnassignedPhotosReturn {
  photos: UnassignedPhotoView[];
  isLoading: boolean;
  error: string | null;
  assignToProject: (photoId: string, projectId: string) => Promise<void>;
  dismissPhoto: (photoId: string) => Promise<void>;
  getSignedUrl: (filePath: string) => Promise<string>;
  refetch: () => Promise<void>;
}

export function useUnassignedPhotos(): UseUnassignedPhotosReturn {
  const effectiveBusinessId = useEffectiveBusinessId();
  const [photos, setPhotos] = useState<UnassignedPhotoView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPhotos = useCallback(async () => {
    if (!effectiveBusinessId) {
      setPhotos([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getUnassignedPhotos(effectiveBusinessId);
      setPhotos(data);
    } catch (err) {
      console.error('Error fetching unassigned photos:', err);
      setError(err instanceof Error ? err.message : 'Failed to load photos');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveBusinessId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const assignToProject = useCallback(async (photoId: string, projectId: string) => {
    if (!effectiveBusinessId) return;

    try {
      await assignPhotoToProject(photoId, projectId, effectiveBusinessId);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      console.error('Error assigning photo:', err);
      throw err;
    }
  }, [effectiveBusinessId]);

  const dismissPhoto = useCallback(async (photoId: string) => {
    try {
      await markPhotoUnassigned(photoId);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      console.error('Error dismissing photo:', err);
      throw err;
    }
  }, []);

  const getSignedUrl = useCallback(async (filePath: string) => {
    return getPhotoUrl(filePath);
  }, []);

  return {
    photos,
    isLoading,
    error,
    assignToProject,
    dismissPhoto,
    getSignedUrl,
    refetch: fetchPhotos,
  };
}